/* FOM · estado da aplicacao.

   Duas metades, separadas de proposito e sem vazamento entre elas:

     SERVER STATE  o que veio do banco pela API — nos, relacoes, mapa,
                   organizacao, historico. Nunca e inventado no cliente e nunca
                   e persistido no navegador como fonte de verdade.

     UI STATE      selecao, zoom, viewport, painel, filtros, modo. Vive so
                   enquanto a aba existe. Nao e dado.

   O banco e a fonte de verdade. A API e a fronteira. Este arquivo e cache com
   regras — nada mais. */
import type {
  FomNode, Relation, Organization, MapInfo, MapCounts, Fingerprint, HistoryEntry, StatusClass,
} from './types.ts';
import { compararIrmaos } from './layout.ts';

export type ServerState = {
  organizacao: Organization | null;
  mapa: MapInfo | null;
  contagens: MapCounts | null;
  nos: FomNode[];
  relacoes: Relation[];
  fingerprint: Fingerprint | null;
  historico: HistoryEntry[];
  /* Posicao escolhida por gente. E SERVER STATE: veio do banco e sobrevive ao
     reload — nao e preferencia de tela. */
  posicoes: Record<string, { x: number; y: number }>;
  carregadoEm: string | null;
};

export type UiState = {
  selecionado: string | null;
  expandidos: Set<string>;
  zoom: number;
  panX: number;
  panY: number;
  filtroSt: Set<StatusClass>;
  mostrarBase: boolean;
  mostrarUser: boolean;
  ligando: string | null;      /* origem de uma relacao em construcao */
  /* CONEXAO selecionada no mapa (gate 0072): a linha e objeto de primeira
     classe — id da relacao semantica (nunca aresta pai/filho) */
  relacaoSelecionada: string | null;
  mensagem: { tipo: 'ok' | 'erro' | 'aviso'; texto: string } | null;
  ocupado: boolean;
};

export type Estado = { server: ServerState; ui: UiState };

const TODOS_ST: StatusClass[] = ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE', 'MIX'];

export function estadoInicial(): Estado {
  return {
    server: {
      organizacao: null, mapa: null, contagens: null,
      nos: [], relacoes: [], fingerprint: null, historico: [], posicoes: {},
      carregadoEm: null,
    },
    ui: {
      selecionado: null,
      expandidos: new Set(['root']),
      zoom: 1, panX: 0, panY: 0,
      filtroSt: new Set(TODOS_ST),
      mostrarBase: true, mostrarUser: true,
      ligando: null, relacaoSelecionada: null, mensagem: null, ocupado: false,
    },
  };
}

type Ouvinte = (e: Estado) => void;

export function criarStore(inicial: Estado = estadoInicial()) {
  let estado = inicial;
  const ouvintes = new Set<Ouvinte>();

  function notificar(): void {
    for (const o of ouvintes) o(estado);
  }

  return {
    get estado(): Estado { return estado; },

    inscrever(fn: Ouvinte): () => void {
      ouvintes.add(fn);
      return () => { ouvintes.delete(fn); };
    },

    /* ------------------------------------------------------ server state -- */

    /** Substitui o retrato do servidor inteiro. Usado na carga e no reload. */
    /* As posicoes sao opcionais: quem ainda nao as leu nao deve zera-las.
       Ausencia nao e o mesmo que "nenhuma posicao". */
    definirMapa(dados: {
      organizacao: Organization; mapa: MapInfo; contagens: MapCounts;
      nos: FomNode[]; relacoes: Relation[]; fingerprint: Fingerprint | null;
      historico?: HistoryEntry[];
      posicoes?: Record<string, { x: number; y: number }>;
    }): void {
      estado = {
        ...estado,
        server: {
          organizacao: dados.organizacao,
          mapa: dados.mapa,
          contagens: dados.contagens,
          nos: dados.nos,
          relacoes: dados.relacoes,
          fingerprint: dados.fingerprint,
          historico: dados.historico ?? estado.server.historico,
          posicoes: dados.posicoes ?? estado.server.posicoes,
          carregadoEm: new Date().toISOString(),
        },
      };
      notificar();
    },

    /** Insere ou substitui UM no vindo da API. Nunca inventa campo. */
    aplicarNo(no: FomNode): void {
      const i = estado.server.nos.findIndex((n) => n.external_id === no.external_id);
      const nos = i >= 0
        ? estado.server.nos.map((n, j) => (j === i ? no : n))
        : [...estado.server.nos, no];
      estado = { ...estado, server: { ...estado.server, nos } };
      notificar();
    },

    /** Remocao e LOGICA no backend; aqui o no some da lista visivel. */
    removerNo(externalId: string): void {
      const alvo = new Set<string>([externalId]);
      let mudou = true;
      while (mudou) {
        mudou = false;
        for (const n of estado.server.nos) {
          if (n.parent_external_id && alvo.has(n.parent_external_id) && !alvo.has(n.external_id)) {
            alvo.add(n.external_id);
            mudou = true;
          }
        }
      }
      const nos = estado.server.nos.filter((n) => !alvo.has(n.external_id));
      const relacoes = estado.server.relacoes.filter(
        (r) => !alvo.has(r.from) && !alvo.has(r.to),
      );
      estado = {
        ...estado,
        server: { ...estado.server, nos, relacoes },
        ui: { ...estado.ui, selecionado: alvo.has(estado.ui.selecionado ?? '') ? null : estado.ui.selecionado },
      };
      notificar();
    },

    /* Aplica o deslocamento de um arrasto. O servidor continua sendo a
       verdade: isto e apenas o eco otimista do que ja foi enviado, e um
       recarregamento traz de volta o que o banco disser. */
    aplicarPosicao(externalId: string, x: number, y: number): void {
      estado = {
        ...estado,
        server: {
          ...estado.server,
          posicoes: { ...estado.server.posicoes, [externalId]: { x, y } },
        },
      };
      notificar();
    },

    removerRelacao(id: string): void {
      estado = {
        ...estado,
        server: { ...estado.server, relacoes: estado.server.relacoes.filter((r) => r.id !== id) },
      };
      notificar();
    },

    aplicarRelacao(rel: Relation): void {
      const i = estado.server.relacoes.findIndex((r) => r.id === rel.id);
      const relacoes = i >= 0
        ? estado.server.relacoes.map((r, j) => (j === i ? rel : r))
        : [...estado.server.relacoes, rel];
      estado = { ...estado, server: { ...estado.server, relacoes } };
      notificar();
    },

    definirHistorico(entries: HistoryEntry[]): void {
      estado = { ...estado, server: { ...estado.server, historico: entries } };
      notificar();
    },

    definirFingerprint(f: Fingerprint): void {
      estado = { ...estado, server: { ...estado.server, fingerprint: f } };
      notificar();
    },

    /* ---------------------------------------------------------- ui state -- */
    patchUi(parcial: Partial<UiState>): void {
      estado = { ...estado, ui: { ...estado.ui, ...parcial } };
      notificar();
    },

    selecionar(externalId: string | null): void {
      /* selecionar um NO desfaz a selecao de conexao: um foco por vez */
      estado = { ...estado, ui: { ...estado.ui, selecionado: externalId, relacaoSelecionada: null } };
      notificar();
    },

    /* selecionar uma CONEXAO: o painel passa a descrever a relacao; o no
       deixa de ser o foco. `null` limpa. */
    selecionarRelacao(id: string | null): void {
      estado = {
        ...estado,
        ui: { ...estado.ui, relacaoSelecionada: id, selecionado: id ? null : estado.ui.selecionado },
      };
      notificar();
    },

    alternarExpandido(externalId: string): void {
      const e = new Set(estado.ui.expandidos);
      if (e.has(externalId)) e.delete(externalId);
      else e.add(externalId);
      estado = { ...estado, ui: { ...estado.ui, expandidos: e } };
      notificar();
    },

    expandirCaminho(externalId: string): void {
      const porId = new Map(estado.server.nos.map((n) => [n.external_id, n]));
      const e = new Set(estado.ui.expandidos);
      let atual = porId.get(externalId);
      while (atual?.parent_external_id) {
        e.add(atual.parent_external_id);
        atual = porId.get(atual.parent_external_id);
      }
      estado = { ...estado, ui: { ...estado.ui, expandidos: e } };
      notificar();
    },

    mensagem(tipo: 'ok' | 'erro' | 'aviso', texto: string): void {
      estado = { ...estado, ui: { ...estado.ui, mensagem: { tipo, texto } } };
      notificar();
    },

    limparMensagem(): void {
      if (!estado.ui.mensagem) return;
      estado = { ...estado, ui: { ...estado.ui, mensagem: null } };
      notificar();
    },

    ocupado(v: boolean): void {
      estado = { ...estado, ui: { ...estado.ui, ocupado: v } };
      notificar();
    },
  };
}

export type Store = ReturnType<typeof criarStore>;

/* ---------------------------------------------------------------- ajudas -- */

export function noPorId(estado: Estado, externalId: string): FomNode | undefined {
  return estado.server.nos.find((n) => n.external_id === externalId);
}

export function filhosDe(estado: Estado, externalId: string): FomNode[] {
  return estado.server.nos
    .filter((n) => n.parent_external_id === externalId)
    .sort(compararIrmaos);
}

export function relacoesDe(estado: Estado, externalId: string): Relation[] {
  return estado.server.relacoes.filter((r) => r.from === externalId || r.to === externalId);
}
